import { Input, Modal, message, Spin } from 'antd';
import { Building2, Layers3, Plus } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Empty } from '@/components/common/Empty';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { brandStore } from '@/stores';

function formatUnixSeconds(value: string) {
  const unixSeconds = Number(value);
  if (!Number.isFinite(unixSeconds) || unixSeconds <= 0) {
    return '-';
  }

  return new Date(unixSeconds * 1000).toLocaleString('zh-CN');
}

export const BrandManagementPage = observer(() => {
  const navigate = useNavigate();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [draftBrandName, setDraftBrandName] = useState('');
  const [draftAppId, setDraftAppId] = useState('');

  useEffect(() => {
    const loadData = async () => {
      if (!brandStore.initialized && !brandStore.loading) {
        await brandStore.fetchBrands();
      }
      await brandStore.fetchWaitlistTotals();
    };

    void loadData().catch(() => undefined);
  }, []);

  const handleOpenBrand = (brandId: string) => {
    const selected = brandStore.setActiveBrand(brandId);
    if (!selected) {
      return;
    }
    navigate(`/brands/${brandId}/beta-management`);
  };

  const handleOpenCreateModal = () => {
    setDraftBrandName('');
    setDraftAppId('');
    setCreateModalOpen(true);
  };

  const handleCreateBrand = async () => {
    try {
      const created = await brandStore.createBrand(draftBrandName, draftAppId);
      await brandStore.fetchWaitlistTotals();
      setCreateModalOpen(false);
      setDraftBrandName('');
      setDraftAppId('');
      message.success(`Brand ${created.name} 创建成功`);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : '创建 Brand 失败，请重试';
      message.error(errorMessage);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-border shadow-sm">
      <div className="p-6 space-y-6">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <h1 className="text-lg font-semibold text-foreground">
              Brand 管理
            </h1>
            <p className="text-sm text-muted-foreground">
              全局查看 Brand 数量，并切换到指定 Brand 的运营配置空间。
            </p>
          </div>
          <Button onClick={handleOpenCreateModal}>
            <Plus className="mr-2 h-4 w-4" />
            新增 Brand
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-border bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Brand 总数</span>
              <Layers3 className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="mt-2 text-3xl font-semibold text-foreground">
              {brandStore.brandCount}
            </div>
          </div>

          <div className="rounded-lg border border-border bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">当前选中</span>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="mt-2 text-3xl font-semibold text-foreground">
              {brandStore.activeBrand?.name || '-'}
            </div>
          </div>
        </div>

        {brandStore.error ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
            加载 Brand 失败：{brandStore.error}
          </div>
        ) : null}

        <div className="overflow-x-auto rounded-lg border border-border">
          <Spin spinning={brandStore.loading} tip="加载 Brand 中...">
            {brandStore.sortedBrands.length === 0 ? (
              <Empty description="暂无 Brand，请先创建" className="py-10" />
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="px-4 py-3">Brand ID</th>
                    <th className="px-4 py-3">Brand 名称</th>
                    <th className="px-4 py-3">App ID</th>
                    <th className="px-4 py-3">创建时间</th>
                    <th className="px-4 py-3">Waitlist 数量</th>
                    <th className="px-4 py-3">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {brandStore.sortedBrands.map((brand) => {
                    const isCurrent = brandStore.activeBrandId === brand.id;
                    const waitlistTotal = brandStore.getWaitlistTotal(brand.id);
                    const isOjo = brand.name.trim().toLowerCase() === 'ojo';
                    return (
                      <tr key={brand.id} className="border-t border-border">
                        <td className="px-4 py-3 font-mono text-muted-foreground">
                          {brand.id}
                        </td>
                        <td className="px-4 py-3 font-medium text-foreground">
                          {brand.name}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-muted-foreground">
                              {brand.appId}
                            </span>
                            {!brand.customAppId ? (
                              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                                默认
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {formatUnixSeconds(brand.createdAt)}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {brandStore.waitlistTotalsLoading &&
                          waitlistTotal === undefined
                            ? '加载中...'
                            : (waitlistTotal ?? '-')}
                          {isOjo ? (
                            <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                              OJO 旧逻辑
                            </span>
                          ) : null}
                        </td>
                        <td className="px-4 py-3">
                          <Button
                            size="sm"
                            variant={isCurrent ? 'outline' : 'default'}
                            onClick={() => handleOpenBrand(brand.id)}
                          >
                            {isCurrent ? '当前 Brand' : '进入运营配置'}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Spin>
        </div>
      </div>

      <Modal
        open={createModalOpen}
        title="新增 Brand"
        centered
        onCancel={() => setCreateModalOpen(false)}
        onOk={() => void handleCreateBrand()}
        okText={brandStore.creating ? '创建中...' : '确认创建'}
        cancelText="取消"
        confirmLoading={brandStore.creating}
      >
        <div className="space-y-3 pt-2">
          <p className="text-sm text-muted-foreground">
            Brand 名称必填，App ID 可选；如果不填，后续相关能力默认沿用 Brand ID。
          </p>
          <div className="space-y-2">
            <Label htmlFor="brand-name-input">Brand 名称 *</Label>
            <Input
              id="brand-name-input"
              value={draftBrandName}
              onChange={(event) => setDraftBrandName(event.target.value)}
              placeholder="例如：OJO"
              maxLength={30}
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              这是 Brand 的展示名称，会显示在 Brand 列表和侧边栏里。
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="brand-app-id-input">App ID（可选）</Label>
            <Input
              id="brand-app-id-input"
              value={draftAppId}
              onChange={(event) => setDraftAppId(event.target.value)}
              placeholder="例如：admin / ojo-web / 233749"
              maxLength={128}
            />
            <p className="text-xs text-muted-foreground">
              这是 account / 登录码等能力使用的应用标识。不填时默认使用生成后的 Brand ID。
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
});
